import { useEffect, useRef } from 'react'

import { Box, Button, Radio, Text } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUnterminateEmployee } from 'modules/employees/hooks/useUnterminateEmployee'
import Employee from 'modules/employees/store/models/Employee'

interface Props {
  onClose: () => void
  employee: Employee
}

const ActivateModal = ({ onClose, employee }: Props) => {
  const { unterminate } = useUnterminateEmployee()
  const defaultValue = employee.hasRegularAccess(hasFeature('employee_onboarding'))
    ? Employee.ACCESS_TYPE.regular
    : Employee.ACCESS_TYPE.welcome_space

  const form = useRef(
    new FactorialForm(
      {
        access_type: defaultValue,
      },
      {
        access_type: 'string',
      }
    )
  ).current

  async function unterminateEmployee() {
    const { data, error } = await unterminate({ id: employee.id })

    if (data?.employee?.id) {
      employee.set({ terminated_on: null })
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: error || i18n.t('common.save_error'),
        time: 2000,
      })
    }
  }

  const { running, performAction: onSubmit } = useAsyncAction(async () => {
    const regularAccessStartsOn =
      form.get('access_type').value === Employee.ACCESS_TYPE.regular ? moment() : undefined
    await employee.setRegularAccessStartDate(regularAccessStartsOn)
    await unterminateEmployee()
    onClose()
  })

  useEffect(() => {
    if (!employee.isTerminated) {
      unterminateEmployee()
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps we want to run this effect only once
  }, [])

  // if an employee termination date is in the future
  if (!employee.isTerminated) {
    return null
  }

  return (
    <Modal
      title={i18n.t('employees.untermination.modal.title')}
      description={i18n.t('employees.untermination.modal.description')}
      illustration="add-user"
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary" disabled={running}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={running} submit>
            {i18n.t('employees.untermination.modal.cta')}
          </Button>
        </>
      }
    >
      <FormRow>
        <Box gap="s8">
          <Text.Normal size="200" weight="medium" color="grey800">
            {i18n.t('employees.untermination.access_type_form.title')}
          </Text.Normal>
          <Radio
            name="welcome_space_access"
            description={i18n.t(
              'employees.untermination.access_type_form.welcome_space_option.description'
            )}
            option={{
              label: i18n.t('employees.untermination.access_type_form.welcome_space_option.label'),
              value: Employee.ACCESS_TYPE.welcome_space!,
            }}
            value={form.get('access_type').value}
            onChange={(value) => form.get('access_type').set(value)}
          />
          <Radio
            name="regular_access"
            description={i18n.t(
              'employees.untermination.access_type_form.regular_access_option.description'
            )}
            option={{
              label: i18n.t('employees.untermination.access_type_form.regular_access_option.label'),
              value: Employee.ACCESS_TYPE.regular!,
            }}
            value={form.get('access_type').value}
            onChange={(value) => form.get('access_type').set(value)}
          />
        </Box>
      </FormRow>
    </Modal>
  )
}

export default observer(ActivateModal)
